import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import SearchInput from '../../../../../component/common/Input/SearchInput';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import { StandardButton, EmptyState } from '../../../../../component/common/StandardUI';
import targetIcon from '../../../../../assets/svg/icons/Target--Streamline-Flex.svg';
import checklistIcon from '../../../../../assets/svg/icons/List-To-Do-Tasks-Checklist--Streamline-Flex.svg';
import ChecklistCheckbox from '../components/ChecklistCheckbox';
import { RecycleBinIcon } from '../components/icons';

const PAGE_SIZE = 10;

const checklistCompletion = (checklist) => {
  if (!Array.isArray(checklist) || checklist.length === 0) return 0;
  const completed = checklist.filter((item) => item.isCompleted).length;
  return Math.round((completed / checklist.length) * 100);
};

const CardShell = ({ onClick, onDelete, canDelete, children }) => (
  <div className="relative rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-5 hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors group">
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-4 right-4 text-red-500 hover:text-red-600 transition-colors"
        aria-label="Delete"
      >
        <RecycleBinIcon className="w-4 h-4" />
      </button>
    )}
    <button type="button" onClick={onClick} className="text-left w-full">
      {children}
    </button>
  </div>
);

const GoalCard = ({ goal, onClick, onDelete, canDelete }) => {
  const percentage = checklistCompletion(goal.checklist);
  return (
    <CardShell onClick={onClick} onDelete={onDelete} canDelete={canDelete}>
      <div className="flex items-start gap-3">
        <img src={targetIcon} alt="" className="w-5 h-5 shrink-0 mt-0.5 dark:invert" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-700 dark:text-gray-200">{goal.title}</p>
          <p className="text-xs text-gray-400 text-right mt-3">{percentage}% until goal reached</p>
          <div className="mt-1 h-1.5 w-full rounded-full bg-shoorah-progressGreenLight dark:bg-shoorah-progressGreen/30 overflow-hidden">
            <div
              className="h-full rounded-full bg-shoorah-progressGreen"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      </div>
    </CardShell>
  );
};

const MilestoneCard = ({ milestone, onClick, onDelete, canDelete, onToggleItem, togglingId }) => (
  <CardShell onClick={onClick} onDelete={onDelete} canDelete={canDelete}>
    <div className="flex items-start gap-3 mb-3">
      <img src={checklistIcon} alt="" className="w-5 h-5 shrink-0 mt-0.5 dark:invert" />
      <div className="min-w-0">
        <p className="font-medium text-gray-700 dark:text-gray-200">{milestone.title}</p>
        <p className="text-xs text-gray-400 mt-0.5">
          Deadline: {milestone.endDate ? moment(milestone.endDate).format('Do MMMM YYYY') : '—'}
        </p>
      </div>
    </div>
    <div className="space-y-2">
      {(milestone.checklist || []).map((item) => (
        <div
          key={item._id}
          className="flex items-center justify-between gap-2"
          onClick={(e) => {
            e.stopPropagation();
            if (togglingId !== item._id) onToggleItem(milestone._id, item._id);
          }}
        >
          <span className="text-sm text-gray-600 dark:text-gray-300">{item.text}</span>
          <ChecklistCheckbox
            size="sm"
            checked={!!item.isCompleted}
            disabled={togglingId === item._id}
            onToggle={() => onToggleItem(milestone._id, item._id)}
            label={`Mark "${item.text}" complete`}
          />
        </div>
      ))}
    </div>
  </CardShell>
);

const Goals = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isMilestones = location.pathname.startsWith(`/${slug}/people-plan/milestones`);
  const basePath = isMilestones ? `/${slug}/people-plan/milestones` : `/${slug}/people-plan/goals`;
  const isSubAdmin = isCopanySubAdmin();
  const currentUserId = userData?.id;
  // A sub admin may only delete records they created; an admin may delete any.
  const canManageRecord = (item) =>
    !isSubAdmin || (item?.createdBy?._id || item?.createdBy) === currentUserId;

  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: isMilestones ? 'Milestones' : 'Goals', href: basePath, current: true }
  ];

  const [list, setList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadedPages, setLoadedPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  // 'mine' = assigned to the logged-in user (creating without self-assigning does
  // not put it here). 'all' = the whole company for an admin; for a sub admin,
  // everything they are assigned or created.
  // Both roles land on 'all'; sub admins get no tabs, so they stay there.
  const defaultScope = 'all';
  const [scope, setScope] = useState(defaultScope);

  // "View more" appends the next page instead of replacing the list.
  const fetchList = useCallback(
    async (page = 1, search = searchTerm, append = false, listScope = scope) => {
      try {
        if (append) setLoadingMore(true);
        else setLoader(true);
        const filters = { search, scope: listScope };
        const response = isMilestones
          ? await CompanyApi.getAllMilestones(page, PAGE_SIZE, filters)
          : await CompanyApi.getAllGoals(page, PAGE_SIZE, filters);
        if (response?.data?.meta?.code === 1) {
          const data = response?.data?.data || [];
          setList((prev) => (append ? [...prev, ...data] : data));
          setTotalCount(response?.data?.meta?.total || 0);
          setLoadedPages(page);
        } else if (!append) {
          setList([]);
          setTotalCount(0);
        }
      } catch (error) {
        errorToast(error.message || 'Failed to load data');
      } finally {
        setLoader(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMilestones]
  );

  useEffect(() => {
    setSearchTerm('');
    setScope(defaultScope);
    fetchList(1, '', false, defaultScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMilestones]);

  const hasMore = list.length < totalCount;

  // scope is passed explicitly — fetchList is memoised on isMilestones, so its
  // default parameters would close over the scope from the first render.
  const handleViewMore = () => fetchList(loadedPages + 1, searchTerm, true, scope);

  const handleScopeChange = (nextScope) => {
    if (nextScope === scope) return;
    setScope(nextScope);
    fetchList(1, searchTerm, false, nextScope);
  };

  // Re-fetch everything loaded so far so the appended pages survive a refresh.
  const refreshList = async () => {
    try {
      setLoader(true);
      const filters = { search: searchTerm, scope };
      const response = isMilestones
        ? await CompanyApi.getAllMilestones(1, PAGE_SIZE * loadedPages, filters)
        : await CompanyApi.getAllGoals(1, PAGE_SIZE * loadedPages, filters);
      if (response?.data?.meta?.code === 1) {
        setList(response?.data?.data || []);
        setTotalCount(response?.data?.meta?.total || 0);
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load data');
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(`Delete this ${isMilestones ? 'milestone' : 'goal'}? This cannot be undone.`)
    )
      return;
    try {
      setLoader(true);
      const response = isMilestones
        ? await CompanyApi.deleteMilestone(id)
        : await CompanyApi.deleteGoal(id);
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Deleted successfully');
        fetchList(1, searchTerm);
      } else {
        setLoader(false);
        errorToast(response?.data?.meta?.message || 'Failed to delete');
      }
    } catch (error) {
      setLoader(false);
      errorToast(error.message || 'Failed to delete');
    }
  };

  const handleToggleItem = async (milestoneId, checklistItemId) => {
    try {
      setTogglingId(checklistItemId);
      const response = await CompanyApi.toggleMilestoneChecklistItem(milestoneId, checklistItemId);
      if (response?.data?.meta?.code === 1) {
        refreshList();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to update checklist item');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to update checklist item');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchList(1, value, false, scope);
  };

  const SCOPE_TABS = [
    { key: 'all', label: 'All' },
    { key: 'mine', label: 'Assign to me' }
  ];

  return (
    <div className="relative lg:mb-[45px]">
      {loader && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>People plan - Goals & Milestones | Shoorah Admin</title>
      </Helmet>
      <Topbar title="People plan" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
          <div>
            <h2 className="text-base font-medium text-gray-700 dark:text-gray-200">
              {isMilestones ? 'Milestones & checklists' : 'Goals'}
            </h2>
            <p className="text-sm text-gray-400">
              {isSubAdmin
                ? `${isMilestones ? 'Milestones' : 'Goals'} assigned to you or created by you.`
                : `Track and manage your company's ${isMilestones ? 'milestones and checklists' : 'goals'
                }.`}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-5 mb-5">
            {/* Sub admins only ever see their own records, so the tabs would be a no-op */}
            {!isSubAdmin && (
              <div className="flex items-center gap-6">
                {SCOPE_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleScopeChange(tab.key)}
                    className={`border-b-2 pb-1.5 text-sm font-medium transition-colors ${scope === tab.key
                        ? 'border-shoorah-btn text-shoorah-btn dark:border-shoorah-btnDark dark:text-white'
                        : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:ml-auto">
              <SearchInput
                id="searchKey"
                name="searchKey"
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={isMilestones ? 'Search milestones' : 'Search goals'}
                inputClassName="!rounded-btn !h-[40px] focus:!border-shoorah-btn focus:!ring-0 dark:focus:!border-shoorah-btnDark"
              />
              <StandardButton
                text={isMilestones ? 'New Milestone' : 'New Goal'}
                onClick={() => navigate(`${basePath}/add`)}
              />
            </div>
          </div>

          {!loader && list.length === 0 && (
            <EmptyState
              size="lg"
              title={`No ${isMilestones ? 'milestones' : 'goals'} yet`}
              description={
                searchTerm
                  ? 'No results match your search.'
                  : `Create a ${isMilestones ? 'milestone' : 'goal'} to start tracking progress.`
              }
              className="border-none"
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {list.map((item) =>
              isMilestones ? (
                <MilestoneCard
                  key={item._id}
                  milestone={item}
                  onClick={() => navigate(`${basePath}/view/${item._id}`)}
                  onDelete={() => handleDelete(item._id)}
                  canDelete={canManageRecord(item)}
                  onToggleItem={handleToggleItem}
                  togglingId={togglingId}
                />
              ) : (
                <GoalCard
                  key={item._id}
                  goal={item}
                  onClick={() => navigate(`${basePath}/view/${item._id}`)}
                  onDelete={() => handleDelete(item._id)}
                  canDelete={canManageRecord(item)}
                />
              )
            )}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={handleViewMore}
              disabled={loadingMore}
              className="mt-5 text-sm font-medium text-shoorah-primary hover:underline disabled:opacity-60"
            >
              {loadingMore ? 'Loading...' : 'View more'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Goals;
